import { PageInfo, useBetsUshersQuery, useUsherTransactionLocationsQuery } from '@/generated/graphql';
import {
  IUsherTransactionLocation,
  IUsherTransactionLocationQueryVariable,
  mapGQLUsherTransactionLocation,
} from '@/shared/interfaces/transaction-location';
import { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Tooltip } from 'react-leaflet';
import Backdrop from '@mui/material/Backdrop';
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  InputAdornment,
  InputLabel,
  ListSubheader,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FiSearch } from 'react-icons/fi';
import { IUsher, IUsherQueryVariable, mapGQLUsher } from '@/shared/interfaces';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  py: 0,
  slotProps: {
    paper: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  },
};

interface IDataFilter {
  dateFrom: string;
  dateTo: string;
  minDistanceGap: number;
  selectedUsherId: string;
}

export function TransactionLocationsPage() {
  const today = new Date(
    new Date().toLocaleString('en-US', {
      timeZone: 'Asia/Manila',
    }),
  );
  const defaultDateFilter = {
    dateFrom: `${today.getFullYear()}-${('0' + (today.getMonth() + 1)).slice(-2)}-${('0' + today.getDate()).slice(-2)}`,
    dateTo: `${today.getFullYear()}-${('0' + (today.getMonth() + 1)).slice(-2)}-${('0' + today.getDate()).slice(-2)}`,
  };

  // Define an initial center; for example, use the first coordinate
  const initialPosition: [number, number] = [9.172085, 123.58373];

  const [usherTransactionLocations, setUsherTransactionLocations] = useState<IUsherTransactionLocation[]>([]);
  const [ushers, setUshers] = useState<IUsher[]>([]);
  const [locationQueryVariable, setLocationQueryVariable] = useState<IUsherTransactionLocationQueryVariable>({
    first: 500,
    filterBy: {
      dateFrom: '2024-04-30',
      dateTo: '2026-10-16',
      usherId: '4e1da44d-f57d-44b8-8fab-f098ec6b7f30',
    },
  });
  const [dataFilter, setDataFilter] = useState<IDataFilter>({
    dateFrom: defaultDateFilter.dateFrom,
    dateTo: defaultDateFilter.dateTo,
    minDistanceGap: 50,
    selectedUsherId: '',
  });

  const [usherQueryVariable] = useState<IUsherQueryVariable>({ first: 50 });
  const [showLoading, setShowLoading] = useState(false);
  const [skipLocationsQuery, setSkipLocationsQuery] = useState(true);

  const [pageInfo, setPageInfo] = useState<PageInfo>();
  const [ushersPageInfo, setUshersPageInfo] = useState<PageInfo>();
  const [resetData, setResetData] = useState<boolean>(false);

  const containsText = (text: string, searchText: string) => text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;
  const [searchText, setSearchText] = useState('');
  const searchRef = useRef<HTMLInputElement | null>(null);
  let displayedOptions = useMemo(() => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
    return ushers.filter((usher) => containsText(`${usher.firstName} ${usher.lastName}`, searchText));
  }, [searchText]);

  const getLocationQuery = useUsherTransactionLocationsQuery({
    variables: locationQueryVariable,
    nextFetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: skipLocationsQuery,
    onCompleted: (data) => {
      const mappedLocations: IUsherTransactionLocation[] = [];
      data.transactionLocations.nodes.map((result) => {
        const mapResult: IUsherTransactionLocation = mapGQLUsherTransactionLocation(result);

        mappedLocations.push({
          id: mapResult.id,
          name: mapResult.name,
          latitude: mapResult.latitude,
          longitude: mapResult.longitude,
          datetime: mapResult.datetime,
          username: mapResult.username,
          transactionCode: mapResult.transactionCode,
        });

        if (!showLoading && data.transactionLocations.pageInfo.hasNextPage) {
          setShowLoading(true);
        }
      });
      setTimeout(() => {
        console.log(mappedLocations);
        setUsherTransactionLocations((test) => [...test, ...mappedLocations]);
        setPageInfo(data.transactionLocations.pageInfo);
      }, 230);
    },
  });

  const ushersQuery = useBetsUshersQuery({
    variables: usherQueryVariable,
    nextFetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const mappedUshers: IUsher[] = data.ushers.nodes.map((usher) => mapGQLUsher(usher));

      if (mapGQLUsher.length > 0 && dataFilter.selectedUsherId === '') {
        setDataFilter((prev) => ({ ...prev, selectedUsherId: mappedUshers[0].id }));
      }

      setTimeout(() => {
        setUshers((ushers) => [...ushers, ...mappedUshers]);
        setUshersPageInfo(data.ushers.pageInfo);
      }, 230);
    },
  });

  const handleClickFilter = () => {
    setSkipLocationsQuery(false);
    setLocationQueryVariable({
      ...locationQueryVariable,
      filterBy: {
        ...locationQueryVariable.filterBy,
        usherId: dataFilter.selectedUsherId,
        dateFrom: dataFilter.dateFrom,
        dateTo: dataFilter.dateTo,
        minDistanceGap: dataFilter.minDistanceGap,
      },
    });
  };

  useEffect(() => {
    if (pageInfo && pageInfo.hasNextPage && pageInfo.endCursor) {
      getLocationQuery.fetchMore({
        variables: { ...locationQueryVariable, after: pageInfo.endCursor },
        updateQuery: (prev, { fetchMoreResult }) => fetchMoreResult,
      });
    } else if (pageInfo && !pageInfo.hasNextPage) {
      setResetData(false);
      setShowLoading(false);
    }
    // if (USRtotalCount >= 25 && ushersSalesReport.length > 25 && showLoading) {
    //   setShowLoading(false);
    // }
  }, [pageInfo]);

  useEffect(() => {
    if (ushersPageInfo && ushersPageInfo.hasNextPage && ushersPageInfo.endCursor) {
      ushersQuery.fetchMore({
        variables: { ...usherQueryVariable, after: ushersPageInfo.endCursor },
        updateQuery: (prev, { fetchMoreResult }) => fetchMoreResult,
      });
    }
  }, [ushersPageInfo]);

  useEffect(() => {
    if (resetData) {
      setUsherTransactionLocations([]);
      // setShowLoading(true);
    }
  }, [resetData]);

  const handleChange = (event: SelectChangeEvent) => {
    setUsherTransactionLocations([]);
    setSearchText('');
    setDataFilter((prev) => ({ ...prev, selectedUsherId: event.target.value }));
  };
  return (
    <>
      <Card sx={{ maxWidth: '100%' }}>
        <CardContent>
          <Stack direction={'row'} display={'flex'} justifyContent={'space-between'}>
            <Stack direction={'column'} display={'flex'} justifyContent={'space-between'}>
              <Typography variant="h6" gutterBottom>
                Filters
              </Typography>
              <Stack direction="row" alignItems={'center'} display={'flex'} spacing={2}>
                <FormControl sx={{ m: 1, minWidth: 300 }} size="small">
                  <InputLabel id="demo-select-small-label">Ushers</InputLabel>
                  <Select
                    labelId="demo-select-small-label"
                    id="demo-select-small"
                    value={dataFilter.selectedUsherId}
                    label="Ushers"
                    onChange={handleChange}
                    MenuProps={MenuProps}>
                    <ListSubheader>
                      <TextField
                        size="small"
                        // Autofocus on textfield
                        autoFocus
                        placeholder="Type to search..."
                        fullWidth
                        slotProps={{
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <FiSearch />
                              </InputAdornment>
                            ),
                          },
                        }}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key !== 'Escape') {
                            // Prevents autoselecting item while typing (default Select behaviour)
                            e.stopPropagation();
                          }
                        }}
                      />
                    </ListSubheader>
                    <MenuItem value="" disabled>
                      <em>None</em>
                    </MenuItem>
                    {searchText.length >= 1
                      ? displayedOptions.map((usher, i) => (
                          <MenuItem key={i} value={usher.id}>
                            {usher.firstName} {usher.lastName}
                          </MenuItem>
                        ))
                      : ushers.map((usher, i) => (
                          <MenuItem key={i} value={usher.id}>
                            {usher.firstName} {usher.lastName}
                          </MenuItem>
                        ))}
                  </Select>
                </FormControl>
                <TextField
                  id="standard-basic"
                  type={'date'}
                  label="Date From"
                  variant="standard"
                  value={dataFilter.dateFrom}
                  onChange={(event) => {
                    setDataFilter({ ...dataFilter, dateFrom: event.target.value });
                  }}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  id="standard-basic"
                  type={'date'}
                  label="Date To"
                  variant="standard"
                  value={dataFilter.dateTo}
                  onChange={(event) => {
                    setDataFilter({ ...dataFilter, dateTo: event.target.value });
                  }}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  id="standard-basic"
                  type={'number'}
                  label="Min Distance Between Points"
                  variant="standard"
                  value={dataFilter.minDistanceGap}
                  onChange={(event) => {
                    setDataFilter({ ...dataFilter, minDistanceGap: Number(event.target.value) });
                  }}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <Button variant="contained" onClick={handleClickFilter}>
                  Filter
                </Button>
              </Stack>
            </Stack>
            <Stack direction={'column'} justifyContent={'end'} display={'flex'}>
              <Typography variant="h4" textAlign={'center'} fontWeight={700}>
                {usherTransactionLocations.length}
              </Typography>
              <Typography variant="caption">No. Locations</Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <Container maxWidth={'xl'} sx={{ p: 3 }}>
        <MapContainer center={initialPosition} zoom={12} style={{ height: '75vh', width: '100%' }}>
          <TileLayer
            attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {usherTransactionLocations.map((data, index) => (
            <Marker key={index} position={[data.latitude, data.longitude]}>
              <Tooltip direction="top" offset={[0, -10]} opacity={0.9}>
                <div>
                  Name: <b>{data.name}</b>
                  <br />
                  Username: {data.username}
                  <br />
                  Transaction Code: {data.transactionCode}
                  <br />
                  Datetime: <b>{data.datetime}</b>
                </div>
              </Tooltip>
            </Marker>
          ))}
        </MapContainer>
      </Container>

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme: any) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        // onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}
